// The hand-over to the previous platform, for an organization it still serves.
//
// The route is `videas-legacy`'s (`backend/crates/external/legacy/src/runtime.rs`), mounted only on a
// deployment with `LEGACY_DATABASE_URL`, and deliberately absent from every OpenAPI document: a
// legacy path in a committed contract would be one more place to delete it from. So its answer is
// declared by hand, the one exception to `schema.d.ts` in `src/api/`, and this folder goes with
// that crate.

import { api, withQuery } from '../client'

/**
 * `BridgeOut` in `backend/crates/external/legacy/src/runtime.rs`, field for field. Declared here because
 * no generated type exists for it; change the two together.
 */
export interface LegacyBridge {
  /** The previous platform's hand-over address, `{origin}/auth/bridge/`. The token is not in it. */
  bridge_url: string
  /** Good for thirty seconds, for this person and this organization, and replayable within them. */
  token: string
}

/**
 * A token handing the caller over to the previous platform, for an organization it still serves.
 * The browser has to reach `{bridge_url}?token=…` as a **top-level GET**: the verifier over there
 * answers nothing else, a POSTed form included.
 *
 * Safe to ask before anything says the organization is still over there, because the refusals are
 * the answer:
 * - `400 validation`: this backend serves the organization, it never lived over there, or the
 *   caller holds a staff seat that operates the platform, which opens every organization here;
 * - `404`: this deployment mounts no bridge, or the caller is not a live member of it;
 * - `503`: a legacy database is configured and the hand-over's key or address is not;
 * - `401`: no session.
 *
 * The trailing slash is the route's own: it is mounted at `/api/app/legacy/bridge/`, and axum
 * matches a path exactly.
 */
export function getLegacyBridge(organizationId: string, signal?: AbortSignal): Promise<LegacyBridge> {
  return api.get<LegacyBridge>(
    withQuery('/legacy/bridge/', { organization_uid: organizationId }),
    undefined,
    signal,
  )
}
